import React, { createContext, useContext, useState } from 'react';

const AuthContext = createContext();

// Reading localStorage in a useEffect meant `user` was still null on the very
// first render after a hard refresh, so PrivateRoute's `user ? children :
// <Navigate to="/login" />` check redirected to login before the effect ever
// ran. Reading it synchronously via the useState initializer fixes that.
const getStoredUser = () => {
    try {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : null;
    } catch {
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const [user, setUser] = useState(getStoredUser);

    const login = ({ token, user }) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setToken(token);
        setUser(user);
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
    };

    const isLoggedIn = !!token;
    // `admin` comes straight from the DB column via the login response and
    // isn't guaranteed to be a JS number (PDO/JSON serialization can hand
    // back "1"/1/true depending on driver config) - Number(...) === 1
    // normalizes all of those the same way the old Angular app's `== 1`
    // loose check did.
    const isAdmin = Number(user?.admin) === 1;

    return (
        <AuthContext.Provider value={{ token, user, isLoggedIn, isAdmin, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
